/**
 * Change a follow-up action's owner and due date from Repair Tracking. The
 * owner is a party on the inspection, named as the inspection has it now, or
 * any team member, found by name (docs/inspections-platform.md, section 10).
 */

import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  type InspectionDetail,
  type PunchListItem,
  useActionOwnerLabel,
  useInspectionChoices,
  useUpdatePunchListItem,
} from '@/admin/hooks/use-inspections'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm, type FormField } from '@/shared/lib/forms'
import { fieldErrorsFromApiError } from '@/admin/pages/settings/member-attribute-helpers'

const FORM_ID = 'punch-list-item-form'
// "No owner"; a select item cannot have an empty value.
const NO_OWNER = 'none'

interface PunchListItemDialogProps {
  inspection: InspectionDetail
  /** The action to change; null keeps the dialog closed. */
  item: PunchListItem | null
  onClose: () => void
}

export function PunchListItemDialog({ inspection, item, onClose }: PunchListItemDialogProps) {
  const { t } = useTranslation()
  const ownerLabel = useActionOwnerLabel()
  const providers = useResourceLookupProviders()
  const { data: choices } = useInspectionChoices()
  const update = useUpdatePunchListItem(inspection.uuid)

  const fields = useMemo<FormField[]>(() => {
    // Each party by name, as the inspection has it now.
    const names: Record<string, string> = {
      contractor: inspection.contractor_name ?? '',
      consumer: inspection.consumer_name || inspection.consumer_email || '',
      inspector: inspection.inspector_name || inspection.inspector_email || '',
    }
    return [
      {
        type: 'select',
        name: 'owner',
        label: t('inspections.actionOwnerLabel', 'Owner'),
        value: NO_OWNER,
        options: [
          { value: NO_OWNER, label: t('inspections.actionNoOwner', 'No owner') },
          ...(choices?.action_owners ?? []).map((owner) => {
            const label = ownerLabel(owner.value, owner.label)
            const name = names[owner.value]
            return { value: owner.value, label: name ? `${label} - ${name}` : label }
          }),
        ],
        testId: 'punch-list-item-owner',
      },
      {
        type: 'entity-search',
        name: 'owner_user',
        label: t('inspections.actionTeamMemberLabel', 'Team member'),
        placeholder: t('inspections.actionTeamMemberPlaceholder', 'Find a team member'),
        value: null,
        configurationData: { lookup_provider: 'inspector', icon: 'user' },
        required: t('inspections.actionTeamMemberRequired', 'Choose a team member'),
        visible: (ctx) => ctx.values.owner === 'user',
        testId: 'punch-list-item-owner-user',
      },
      {
        type: 'datepicker',
        name: 'due_date',
        label: t('inspections.actionDueDateLabel', 'Due date'),
        hint: t('inspections.actionDueDateHint', 'Blank means no due date.'),
        value: '',
        testId: 'punch-list-item-due-date',
      },
    ]
  }, [inspection, choices, ownerLabel, t])

  const { Form, ref } = useForm(
    { name: FORM_ID, submit: 'footer', fields },
    async (values, formCtx) => {
      if (!item) return
      const v = values as {
        owner?: string
        owner_user?: { uuid?: string } | null
        due_date?: string
      }
      const owner = !v.owner || v.owner === NO_OWNER ? '' : v.owner
      try {
        await update.mutateAsync({
          itemUuid: item.uuid,
          owner,
          owner_user_uuid: owner === 'user' ? (v.owner_user?.uuid ?? null) : null,
          due_date: v.due_date || null,
        })
        toast.success(t('inspections.actionSaved', 'Action updated.'))
        onClose()
      } catch (err) {
        const fieldErrors = fieldErrorsFromApiError(err)
        if (fieldErrors.owner_user_uuid) {
          formCtx.setFieldError('owner_user', fieldErrors.owner_user_uuid)
        } else {
          toastApiError(err, t('inspections.actionSaveFailed', 'Could not update the action.'))
        }
      }
    },
    [item, update, onClose, t],
  )

  // Seed the form with the action's owner and due date each time it opens.
  useEffect(() => {
    if (item) {
      ref.current?.resetForm({
        values: {
          owner: item.owner || NO_OWNER,
          owner_user:
            item.owner === 'user' && item.owner_user_uuid
              ? { uuid: item.owner_user_uuid, name: item.owner_name }
              : null,
          due_date: item.due_date ?? '',
        },
      })
    }
  }, [item, ref])

  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[15px] text-[var(--ink)]">
            {t('inspections.actionEditTitle', 'Edit action')}
          </DialogTitle>
        </DialogHeader>
        {item && (
          <p className="whitespace-pre-line text-xs text-[var(--ink-3)]">{item.description}</p>
        )}
        <ResourceLookupContextProvider providers={providers}>
          <Form className="space-y-4" />
        </ResourceLookupContextProvider>
        <DialogFooter className="flex justify-end gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button type="submit" form={FORM_ID} disabled={update.isPending}>
            {update.isPending && <Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" />}
            {t('common.saveChanges', 'Save changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
